import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Spin } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

import useFetchLensConfig from 'hooks/ppa/useFetchLensConfig';
import useUpdateLens from 'hooks/ppa/useUpdateLens';

import type { LensConfigApiResponse } from './customLens.api.types';
import {
    buildSystemLensUpdateRequest,
    parseSystemLensEditFormFromApi,
} from './customLens.helpers';
import { LensEditWarningBanner, LensModalStyled } from './customLens.styled';
import { CUSTOM_LENS_UI, CUSTOM_LENS_WARNING_BANNER_STYLE } from './customLens.ui';
import type { EditSystemLensModalProps, SystemLensEditFormData } from './customLens.types';
import SelectedItemsPanel from './SelectedItemsPanel';

const EditSystemLensModal: FC<EditSystemLensModalProps> = ({
    open = false,
    lensId = null,
    onCancel = () => {},
    onOk = () => {},
}) => {
    const {
        data: lensConfigData,
        isLoading: isLensConfigLoading,
        isPending: isLensConfigPending,
    } = useFetchLensConfig(lensId, open);
    const { loading: isSaving, mutate: updateLens } = useUpdateLens();

    const apiResponse = lensConfigData?.raw as LensConfigApiResponse | undefined;
    const initialForm = useMemo(
        () => (lensConfigData?.raw ? parseSystemLensEditFormFromApi(lensConfigData.raw) : null),
        [lensConfigData?.raw],
    );

    const [formState, setFormState] = useState<SystemLensEditFormData | null>(null);

    useEffect(() => {
        if (!open) {
            setFormState(null);
            return;
        }

        if (initialForm) {
            setFormState(initialForm);
        }
    }, [open, initialForm]);

    const isConfigLoading = open && (isLensConfigLoading || isLensConfigPending);
    const modalTitle = formState ? `Edit ${formState.lensName} Lens` : 'Edit Lens';
    const saveButtonLabel = apiResponse?.view_config?.actions?.primary?.label || 'Save';
    const cancelButtonLabel = apiResponse?.view_config?.actions?.secondary?.label || 'Cancel';
    const buttonStyle = { borderRadius: CUSTOM_LENS_UI.BUTTON_BORDER_RADIUS };

    const handleSave = () => {
        if (!formState || isSaving || !apiResponse?.selected_items) {
            return;
        }

        const payload = buildSystemLensUpdateRequest(
            formState,
            apiResponse.selected_items,
            apiResponse.catalog?.sections,
            apiResponse,
        );

        updateLens(
            {
                lensId: formState.lensId,
                payload,
            },
            {
                onSuccess: () => {
                    onOk();
                },
            },
        );
    };

    const modalFooter = (
        <div className="flex w-full justify-end gap-2">
            <Button style={buttonStyle} onClick={onCancel}>
                {cancelButtonLabel}
            </Button>
            <Button
                type="primary"
                style={buttonStyle}
                disabled={!formState || isSaving}
                loading={isSaving}
                onClick={handleSave}
            >
                {saveButtonLabel}
            </Button>
        </div>
    );

    return (
        <LensModalStyled
            open={open}
            title={modalTitle}
            footer={modalFooter}
            width={1040}
            centered
            maskClosable={false}
            destroyOnClose
            onCancel={onCancel}
            bodyStyle={{ padding: '0px', height: '500px' }}
            style={{ borderRadius: CUSTOM_LENS_UI.MODAL_BORDER_RADIUS }}
        >
            <div className="flex h-full flex-col gap-2">
                <LensEditWarningBanner>
                    <ExclamationCircleFilled
                        className="mt-0.5 shrink-0 text-base"
                        style={{ color: CUSTOM_LENS_WARNING_BANNER_STYLE.iconColor }}
                    />
                    <span>
                        Any change to this lens will impact all scenarios and views where it is being used.
                    </span>
                </LensEditWarningBanner>

                {isConfigLoading || !formState ? (
                    <div className="flex min-h-[420px] flex-1 items-center justify-center border border-borderGreyAlt bg-white">
                        <Spin />
                    </div>
                ) : (
                    <div className="flex h-full flex-1 overflow-hidden border border-borderGreyAlt bg-white">
                        <SelectedItemsPanel
                            title="Dimensions"
                            emptyText="No dimensions configured"
                            items={formState.dimensionOrder}
                            onReorder={(dimensionOrder) =>
                                setFormState((current) => (current ? { ...current, dimensionOrder } : current))
                            }
                            showRemoveButton={false}
                            className="relative flex-1 after:absolute after:bottom-3 after:right-0 after:top-3 after:w-px after:bg-borderGreyAlt after:content-['']"
                        />
                        <SelectedItemsPanel
                            title="User Inputs / KPIs"
                            emptyText="No user inputs configured"
                            items={formState.kpiOrder}
                            onReorder={(kpiOrder) =>
                                setFormState((current) => (current ? { ...current, kpiOrder } : current))
                            }
                            showRemoveButton={false}
                            className="flex-1"
                        />
                    </div>
                )}
            </div>
        </LensModalStyled>
    );
};

export default EditSystemLensModal;
